import { useState, useEffect } from "react";
import { useAuth } from "../../../contexts/AuthContext";
import { useTheme } from "../../../contexts/ThemeContext";
import { supabase, patchProfile } from "../../../lib/supabase";
import AvatarUploader from "./AvatarUploader";

const SUPABASE_FUNCTIONS_URL = import.meta.env.VITE_PUBLIC_SUPABASE_URL
  ? `${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1`
  : "";

export default function SettingsTab() {
  return (
    <div className="max-w-2xl space-y-6">
      <ProfileSection />
      <PlanSection />
      <SecuritySection />
      <TwoFASection />
      <AccountSection />
    </div>
  );
}

// ─── Shared helpers ────────────────────────────────────────────────────────────
async function getActiveSession() {
  const { data: { session } } = await supabase.auth.getSession();
  return session;
}

function validatePassword(pwd: string): string | null {
  if (pwd.length < 8) return "Password must be at least 8 characters";
  if (!/[0-9]/.test(pwd)) return "Must contain at least 1 number";
  if (!/[^a-zA-Z0-9]/.test(pwd)) return "Must contain at least 1 special character";
  return null;
}

// ─── Shared card wrapper ───────────────────────────────────────────────────────
function SettingsCard({
  title, icon, children,
}: {
  title: string;
  icon: string;
  children: React.ReactNode;
}) {
  const { isDark } = useTheme();
  return (
    <div className={`rounded-2xl border p-6 ${isDark ? "bg-[#0d0d0d] border-white/6" : "bg-[#f8f8f8] border-black/6"}`}>
      <div className="flex items-center gap-2.5 mb-6">
        <i className={`${icon} text-sm ${isDark ? "text-[#555]" : "text-[#999]"}`} />
        <h3 className={`font-syne font-semibold text-base ${isDark ? "text-white" : "text-[#111]"}`}>{title}</h3>
      </div>
      {children}
    </div>
  );
}

// ─── Toast helper ──────────────────────────────────────────────────────────────
function Toast({ toast, isDark }: { toast: { msg: string; ok: boolean } | null; isDark: boolean }) {
  if (!toast) return null;
  return (
    <span className={`font-inter text-xs font-medium ${toast.ok ? (isDark ? "text-emerald-400" : "text-emerald-600") : (isDark ? "text-red-400" : "text-red-500")}`}>
      {toast.ok
        ? <><i className="ri-checkbox-circle-line mr-1" />{toast.msg}</>
        : <><i className="ri-error-warning-line mr-1" />{toast.msg}</>}
    </span>
  );
}

// ─── Profile Section ───────────────────────────────────────────────────────────
function ProfileSection() {
  const { user, profile, refreshProfile } = useAuth();
  const { isDark } = useTheme();
  const [displayName, setDisplayName] = useState(profile?.display_name ?? "");
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState<{ msg: string; ok: boolean } | null>(null);

  useEffect(() => {
    setDisplayName(profile?.display_name ?? "");
  }, [profile?.display_name]);

  const showToast = (msg: string, ok: boolean) => {
    setToast({ msg, ok });
    setTimeout(() => setToast(null), 5000);
  };

  const handleSave = async () => {
    if (!user) { showToast("Not signed in. Please refresh.", false); return; }
    setSaving(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) { showToast("Session expired — please sign out and back in.", false); return; }

      await patchProfile(session.access_token, user.id, {
        display_name: displayName.trim() || null,
      });
      await refreshProfile();
      showToast("Profile saved!", true);
    } catch (err: unknown) {
      showToast(err instanceof Error ? err.message : "Save failed.", false);
    } finally {
      setSaving(false);
    }
  };

  const inputClass = `w-full px-4 py-2.5 rounded-xl font-inter text-sm border outline-none transition-all duration-150 focus:ring-2 ${
    isDark
      ? "bg-[#1a1a1a] border-white/10 text-white placeholder-[#3a3a3a] focus:ring-white/10 focus:border-white/20"
      : "bg-[#ebebeb] border-black/10 text-[#111] placeholder-[#aaa] focus:ring-black/8 focus:border-black/20"
  }`;

  return (
    <SettingsCard title="Profile" icon="ri-user-3-line">
      {/* Avatar row */}
      <div className="flex items-center gap-5 mb-6 pb-6 border-b" style={{ borderColor: isDark ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.05)" }}>
        <AvatarUploader size="lg" />
        <div>
          <p className={`font-inter text-sm font-medium mb-0.5 ${isDark ? "text-white" : "text-[#111]"}`}>Profile Photo</p>
          <p className={`font-inter text-xs ${isDark ? "text-[#555]" : "text-[#999]"}`}>
            Click the circle to upload a new photo.<br />JPG, PNG or GIF · Max 5MB
          </p>
        </div>
      </div>

      {/* Display name */}
      <div className="mb-4">
        <label className={`block font-inter text-xs font-medium mb-1.5 ${isDark ? "text-[#666]" : "text-[#888]"}`}>
          Display Name
        </label>
        <input
          type="text"
          value={displayName}
          onChange={(e) => setDisplayName(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && handleSave()}
          placeholder="Enter your name"
          maxLength={40}
          className={inputClass}
        />
      </div>

      {/* Email (read-only) */}
      <div className="mb-6">
        <label className={`block font-inter text-xs font-medium mb-1.5 ${isDark ? "text-[#666]" : "text-[#888]"}`}>
          Email Address
        </label>
        <div className={`flex items-center gap-2 w-full px-4 py-2.5 rounded-xl font-inter text-sm border ${isDark ? "bg-white/2 border-white/5 text-[#444]" : "bg-black/2 border-black/5 text-[#bbb]"}`}>
          <i className="ri-lock-2-line text-xs opacity-50" />
          <span>{user?.email}</span>
        </div>
        <p className={`font-inter text-[10px] mt-1 ${isDark ? "text-[#444]" : "text-[#bbb]"}`}>
          Email cannot be changed. Contact support if needed.
        </p>
      </div>

      <div className="flex items-center gap-4 flex-wrap">
        <button
          type="button"
          onClick={handleSave}
          disabled={saving}
          className={`flex items-center gap-2 px-5 py-2.5 rounded-xl font-inter text-sm font-medium cursor-pointer transition-all duration-200 whitespace-nowrap disabled:opacity-50 ${
            isDark ? "bg-white text-black hover:bg-[#e0e0e0]" : "bg-[#111] text-white hover:bg-[#333]"
          }`}
        >
          {saving ? <i className="ri-loader-4-line animate-spin text-xs" /> : <i className="ri-save-3-line text-xs" />}
          {saving ? "Saving…" : "Save Changes"}
        </button>
        <Toast toast={toast} isDark={isDark} />
      </div>
    </SettingsCard>
  );
}

// ─── Security Section (password) ───────────────────────────────────────────────
function SecuritySection() {
  const { isDark } = useTheme();
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState<{ msg: string; ok: boolean } | null>(null);

  const showToast = (msg: string, ok: boolean) => {
    setToast({ msg, ok });
    setTimeout(() => setToast(null), 4000);
  };

  const handleChangePassword = async () => {
    const pwErr = validatePassword(newPassword);
    if (pwErr) { showToast(pwErr, false); return; }
    if (newPassword !== confirmPassword) { showToast("Passwords don\u2019t match.", false); return; }
    setSaving(true);
    try {
      const session = await getActiveSession();
      if (!session) { showToast("Session expired \u2014 please sign out and back in.", false); return; }
      const { error } = await supabase.auth.updateUser({ password: newPassword });
      if (error) throw error;
      setNewPassword("");
      setConfirmPassword("");
      showToast("Password updated successfully!", true);
    } catch (err: unknown) {
      showToast(err instanceof Error ? err.message : "Failed to update password.", false);
    } finally {
      setSaving(false);
    }
  };

  // Password strength
  const getStrength = (pwd: string) => {
    if (!pwd) return { score: 0, label: "", color: "" };
    let score = 0;
    if (pwd.length >= 8) score++;
    if (/[0-9]/.test(pwd)) score++;
    if (/[^a-zA-Z0-9]/.test(pwd)) score++;
    if (pwd.length >= 12) score++;
    return {
      score,
      label: ["", "Weak", "Fair", "Good", "Strong"][score],
      color: ["", "bg-red-500", "bg-amber-400", "bg-emerald-400", "bg-emerald-500"][score],
    };
  };
  const strength = getStrength(newPassword);

  const PasswordInput = (
    show: boolean,
    setter: (v: boolean) => void,
    val: string,
    setVal: (v: string) => void,
    placeholder: string,
  ) => (
    <div className={`flex items-center gap-2 w-full px-4 py-2.5 rounded-xl border transition-all duration-150 focus-within:ring-2 ${
      isDark
        ? "bg-[#1a1a1a] border-white/10 focus-within:ring-white/10 focus-within:border-white/20"
        : "bg-[#ebebeb] border-black/10 focus-within:ring-black/8 focus-within:border-black/20"
    }`}>
      <input
        type={show ? "text" : "password"}
        value={val}
        onChange={(e) => setVal(e.target.value)}
        placeholder={placeholder}
        className={`flex-1 bg-transparent outline-none font-inter text-sm ${isDark ? "text-white placeholder-[#3a3a3a]" : "text-[#111] placeholder-[#aaa]"}`}
      />
      <button
        type="button"
        onClick={() => setter(!show)}
        className={`w-4 h-4 flex items-center justify-center cursor-pointer flex-shrink-0 ${isDark ? "text-[#444] hover:text-[#888]" : "text-[#ccc] hover:text-[#888]"}`}
      >
        <i className={`${show ? "ri-eye-off-line" : "ri-eye-line"} text-xs`} />
      </button>
    </div>
  );

  return (
    <SettingsCard title="Password" icon="ri-lock-password-line">
      <div className="space-y-4">
        <div>
          <label className={`block font-inter text-xs font-medium mb-1.5 ${isDark ? "text-[#666]" : "text-[#888]"}`}>New Password</label>
          {PasswordInput(showNew, setShowNew, newPassword, setNewPassword, "Enter new password")}
          {newPassword.length > 0 && (
            <div className="mt-1.5 space-y-1.5">
              <div className="flex items-center gap-2">
                <div className={`flex-1 h-1 rounded-full overflow-hidden ${isDark ? "bg-white/8" : "bg-black/8"}`}>
                  <div className={`h-full rounded-full transition-all duration-300 ${strength.color}`} style={{ width: `${(strength.score / 4) * 100}%` }} />
                </div>
                {strength.label && (
                  <span className={`font-inter text-[10px] ${strength.score <= 1 ? "text-red-400" : strength.score === 2 ? "text-amber-400" : "text-emerald-400"}`}>
                    {strength.label}
                  </span>
                )}
              </div>
              <div className="flex flex-wrap gap-x-4 gap-y-0.5">
                {[
                  { ok: newPassword.length >= 8, label: "8+ chars" },
                  { ok: /[0-9]/.test(newPassword), label: "1 number" },
                  { ok: /[^a-zA-Z0-9]/.test(newPassword), label: "1 special char" },
                ].map((req) => (
                  <span key={req.label} className={`font-inter text-[10px] flex items-center gap-1 ${req.ok ? "text-emerald-400" : isDark ? "text-[#555]" : "text-[#bbb]"}`}>
                    <i className={`${req.ok ? "ri-checkbox-circle-line" : "ri-circle-line"} text-[10px]`} />
                    {req.label}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
        <div>
          <label className={`block font-inter text-xs font-medium mb-1.5 ${isDark ? "text-[#666]" : "text-[#888]"}`}>Confirm Password</label>
          {PasswordInput(showConfirm, setShowConfirm, confirmPassword, setConfirmPassword, "Re-enter new password")}
          {confirmPassword.length > 0 && newPassword !== confirmPassword && (
            <p className="font-inter text-[10px] mt-1 text-red-400"><i className="ri-error-warning-line mr-1" />Passwords don&apos;t match</p>
          )}
          {confirmPassword.length > 0 && newPassword === confirmPassword && (
            <p className="font-inter text-[10px] mt-1 text-emerald-400"><i className="ri-checkbox-circle-line mr-1" />Passwords match</p>
          )}
        </div>
        <div className="flex items-center gap-4 pt-1 flex-wrap">
          <button
            type="button"
            onClick={handleChangePassword}
            disabled={saving || !newPassword || !confirmPassword}
            className={`flex items-center gap-2 px-5 py-2.5 rounded-xl font-inter text-sm font-medium cursor-pointer transition-all duration-200 whitespace-nowrap disabled:opacity-40 disabled:cursor-not-allowed ${
              isDark ? "bg-white text-black hover:bg-[#e0e0e0]" : "bg-[#111] text-white hover:bg-[#333]"
            }`}
          >
            {saving ? <i className="ri-loader-4-line animate-spin text-xs" /> : <i className="ri-lock-password-line text-xs" />}
            {saving ? "Updating\u2026" : "Update Password"}
          </button>
          <Toast toast={toast} isDark={isDark} />
        </div>
      </div>
    </SettingsCard>
  );
}

// ─── Two-Step Verification Section ────────────────────────────────────────────
function TwoFASection() {
  const { user, profile, refreshProfile } = useAuth();
  const { isDark } = useTheme();
  const [enabled, setEnabled] = useState(profile?.two_fa_enabled ?? false);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState<{ msg: string; ok: boolean } | null>(null);

  useEffect(() => {
    setEnabled(profile?.two_fa_enabled ?? false);
  }, [profile?.two_fa_enabled]);

  const showToast = (msg: string, ok: boolean) => {
    setToast({ msg, ok });
    setTimeout(() => setToast(null), 4000);
  };

  const toggleEnabled = async () => {
    if (!user) return;
    const newEnabled = !enabled;
    setSaving(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) { showToast("Session expired — please sign out and back in.", false); return; }
      await patchProfile(session.access_token, user.id, {
        two_fa_enabled: newEnabled,
      });
      setEnabled(newEnabled);
      await refreshProfile();
      showToast(newEnabled ? "Two-step verification enabled." : "Two-step verification disabled.", true);
    } catch (err: unknown) {
      showToast(err instanceof Error ? err.message : "Save failed.", false);
    } finally {
      setSaving(false);
    }
  };

  return (
    <SettingsCard title="Two-Step Verification" icon="ri-shield-check-line">
      <div className="flex items-center justify-between">
        <div>
          <p className={`font-inter text-sm font-medium mb-0.5 ${isDark ? "text-white" : "text-[#111]"}`}>
            {enabled ? "Enabled" : "Disabled"}
          </p>
          <p className={`font-inter text-xs ${isDark ? "text-[#555]" : "text-[#999]"}`}>
            {enabled
              ? "A code is emailed to you each time you sign in."
              : "Add an extra layer of security to your account."}
          </p>
        </div>
        <button
          type="button"
          onClick={toggleEnabled}
          disabled={saving}
          className={`relative w-11 h-6 rounded-full transition-all duration-200 cursor-pointer flex-shrink-0 disabled:opacity-50 ${
            enabled
              ? isDark ? "bg-white" : "bg-[#111]"
              : isDark ? "bg-white/10" : "bg-black/10"
          }`}
        >
          <span className={`absolute top-0.5 w-5 h-5 rounded-full transition-all duration-200 ${
            enabled
              ? isDark ? "left-[22px] bg-black" : "left-[22px] bg-white"
              : "left-0.5 bg-white"
          }`} />
        </button>
      </div>

      {toast && (
        <div className="mt-4">
          <Toast toast={toast} isDark={isDark} />
        </div>
      )}
    </SettingsCard>
  );
}

// ─── Plan & Billing Section ────────────────────────────────────────────────────
function PlanSection() {
  const { profile, session } = useAuth();
  const { isDark } = useTheme();

  type CancelStep = "idle" | "confirming" | "loading" | "error";
  const [cancelStep, setCancelStep] = useState<CancelStep>("idle");
  const [portalLoading, setPortalLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  const isPro = profile?.plan === "pro";
  const status = profile?.plan_status;
  const billing = profile?.billing_period;
  const isMonthlyActive = isPro && billing === "monthly" && status === "active";
  const isLifetime = isPro && billing === "lifetime";

  const openPortal = async (flow?: "cancel") => {
    if (!session?.access_token) return;
    const isCancel = flow === "cancel";
    if (isCancel) setCancelStep("loading");
    else setPortalLoading(true);
    setErrorMsg(null);

    try {
      const returnUrl = `${window.location.origin}/dashboard?tab=settings`;
      const res = await fetch(`${SUPABASE_FUNCTIONS_URL}/stripe-portal`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({ returnUrl, ...(flow ? { flow } : {}) }),
      });
      const data = await res.json();
      if (data.url) {
        window.location.href = data.url;
      } else {
        throw new Error(data.error ?? "Could not open billing portal.");
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Something went wrong.";
      setErrorMsg(msg);
      if (isCancel) setCancelStep("error");
      else setPortalLoading(false);
    }
  };

  type BadgeVariant = { bg: string; text: string; dot: string; label: string };

  const getBadge = (): BadgeVariant => {
    if (!isPro) return { bg: isDark ? "bg-white/6" : "bg-black/5", text: isDark ? "text-[#777]" : "text-[#888]", dot: "bg-[#888]", label: "Free" };
    if (status === "pending") return { bg: isDark ? "bg-amber-500/12" : "bg-amber-50", text: isDark ? "text-amber-400" : "text-amber-600", dot: "bg-amber-400", label: "Pro — Pending" };
    if (status === "cancelled") return { bg: isDark ? "bg-red-500/10" : "bg-red-50", text: isDark ? "text-red-400" : "text-red-500", dot: "bg-red-400", label: "Pro — Cancelled" };
    return { bg: isDark ? "bg-amber-500/12" : "bg-amber-50", text: isDark ? "text-amber-400" : "text-amber-600", dot: "bg-amber-500", label: "Pro" };
  };

  const badge = getBadge();

  const billingText =
    billing === "monthly" ? "$10 / month" :
    billing === "lifetime" ? "$200 — Lifetime access" :
    "No active subscription";

  const statusNote: Record<string, string> = {
    pending: "Your payment is being confirmed. Access will activate shortly.",
    active: "Your plan is active and in good standing.",
    cancelled: "Your plan has been cancelled. Access continues until the period ends.",
  };

  return (
    <SettingsCard title="Plan & Billing" icon="ri-vip-crown-line">
      {/* Plan badge row */}
      <div className={`flex items-center justify-between p-4 rounded-xl mb-4 ${isDark ? "bg-white/3 border border-white/6" : "bg-black/2 border border-black/5"}`}>
        <div className="flex items-center gap-3">
          <div className={`w-9 h-9 flex items-center justify-center rounded-lg flex-shrink-0 ${isPro ? (isDark ? "bg-amber-500/15" : "bg-amber-50") : (isDark ? "bg-white/5" : "bg-black/5")}`}>
            <i className={`ri-vip-crown-line text-sm ${isPro ? (isDark ? "text-amber-400" : "text-amber-500") : (isDark ? "text-[#555]" : "text-[#bbb]")}`} />
          </div>
          <div>
            <p className={`font-syne font-semibold text-sm ${isDark ? "text-white" : "text-[#111]"}`}>
              {isPro ? "Trackly Pro" : "Trackly Free"}
            </p>
            <p className={`font-inter text-xs mt-0.5 ${isDark ? "text-[#555]" : "text-[#999]"}`}>{billingText}</p>
          </div>
        </div>
        <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full font-inter text-xs font-medium ${badge.bg} ${badge.text}`}>
          <span className={`w-1.5 h-1.5 rounded-full ${badge.dot}`} />
          {badge.label}
        </span>
      </div>

      {/* Status note */}
      {isPro && status && statusNote[status] && (
        <div className={`flex items-start gap-2.5 p-3 rounded-xl mb-4 ${
          status === "cancelled"
            ? isDark ? "bg-red-500/8 border border-red-500/15" : "bg-red-50 border border-red-100"
            : status === "pending"
            ? isDark ? "bg-amber-500/8 border border-amber-500/15" : "bg-amber-50 border border-amber-100"
            : isDark ? "bg-emerald-500/8 border border-emerald-500/15" : "bg-emerald-50 border border-emerald-100"
        }`}>
          <i className={`text-xs mt-0.5 flex-shrink-0 ${
            status === "cancelled" ? (isDark ? "ri-information-line text-red-400" : "ri-information-line text-red-500") :
            status === "pending" ? (isDark ? "ri-time-line text-amber-400" : "ri-time-line text-amber-500") :
            (isDark ? "ri-checkbox-circle-line text-emerald-400" : "ri-checkbox-circle-line text-emerald-500")
          }`} />
          <p className={`font-inter text-xs leading-relaxed ${
            status === "cancelled" ? (isDark ? "text-red-400" : "text-red-500") :
            status === "pending" ? (isDark ? "text-amber-400" : "text-amber-600") :
            (isDark ? "text-emerald-400" : "text-emerald-600")
          }`}>{statusNote[status]}</p>
        </div>
      )}

      {/* Billing detail rows — Pro only */}
      {isPro && billing && (
        <div className={`space-y-0 rounded-xl overflow-hidden border mb-4 ${isDark ? "border-white/5" : "border-black/5"}`}>
          {[
            { label: "Plan type", value: "Trackly Pro", icon: "ri-vip-crown-line" },
            { label: "Billing cycle", value: billing === "lifetime" ? "One-time payment" : "Monthly", icon: "ri-calendar-line" },
            { label: "Amount", value: billing === "lifetime" ? "$200.00" : "$10.00 / mo", icon: "ri-price-tag-3-line" },
          ].map((row, i, arr) => (
            <div
              key={row.label}
              className={`flex items-center justify-between px-4 py-3 ${
                i < arr.length - 1 ? (isDark ? "border-b border-white/5" : "border-b border-black/5") : ""
              } ${isDark ? "bg-white/2" : "bg-black/1"}`}
            >
              <div className="flex items-center gap-2">
                <i className={`${row.icon} text-xs ${isDark ? "text-[#555]" : "text-[#bbb]"}`} />
                <span className={`font-inter text-xs ${isDark ? "text-[#666]" : "text-[#999]"}`}>{row.label}</span>
              </div>
              <span className={`font-inter text-xs font-medium ${isDark ? "text-[#aaa]" : "text-[#444]"}`}>{row.value}</span>
            </div>
          ))}
        </div>
      )}

      {/* ── Monthly active: manage + cancel ─────────────────────────────────── */}
      {isMonthlyActive && (
        <div className={`pt-4 border-t ${isDark ? "border-white/5" : "border-black/5"}`}>

          {/* Cancel confirmation panel */}
          {cancelStep === "confirming" && (
            <div className={`rounded-xl border p-4 mb-4 ${isDark ? "bg-red-500/6 border-red-500/20" : "bg-red-50 border-red-100"}`}>
              <div className="flex items-center gap-2 mb-3">
                <i className={`ri-error-warning-line text-sm ${isDark ? "text-red-400" : "text-red-500"}`} />
                <p className={`font-syne font-semibold text-sm ${isDark ? "text-red-300" : "text-red-700"}`}>
                  Cancel your subscription?
                </p>
              </div>
              <p className={`font-inter text-xs mb-3 ${isDark ? "text-red-400/80" : "text-red-600"}`}>
                You&apos;ll lose access to the following when your billing period ends:
              </p>
              <ul className={`space-y-1.5 mb-4 ${isDark ? "text-red-400/70" : "text-red-500/80"}`}>
                {[
                  "All Pro features at launch",
                  "Priority & dedicated support",
                  "Your locked-in $10/month pricing",
                  "Advanced analytics & ROI tracking",
                ].map((item) => (
                  <li key={item} className="flex items-center gap-2 font-inter text-xs">
                    <i className="ri-close-circle-line text-xs flex-shrink-0" />
                    {item}
                  </li>
                ))}
              </ul>
              <p className={`font-inter text-[11px] mb-4 ${isDark ? "text-[#555]" : "text-[#999]"}`}>
                Your access continues until the end of the current billing period.
              </p>
              <div className="flex items-center gap-2 flex-wrap">
                <button
                  type="button"
                  onClick={() => setCancelStep("idle")}
                  className={`flex items-center gap-1.5 px-4 py-2 rounded-lg font-inter text-xs font-medium whitespace-nowrap cursor-pointer transition-all duration-200 ${
                    isDark ? "bg-white text-black hover:bg-[#e0e0e0]" : "bg-[#111] text-white hover:bg-[#333]"
                  }`}
                >
                  <i className="ri-heart-line text-[11px]" />
                  Keep my plan
                </button>
                <button
                  type="button"
                  onClick={() => openPortal("cancel")}
                  className={`flex items-center gap-1.5 px-4 py-2 rounded-lg font-inter text-xs font-medium whitespace-nowrap cursor-pointer transition-all duration-200 border ${
                    isDark
                      ? "border-red-500/30 text-red-400 hover:bg-red-500/10"
                      : "border-red-300 text-red-600 hover:bg-red-50"
                  }`}
                >
                  <i className="ri-external-link-line text-[11px]" />
                  Continue to cancellation
                </button>
              </div>
            </div>
          )}

          {/* Error state */}
          {cancelStep === "error" && errorMsg && (
            <div className={`flex items-start gap-2.5 p-3 rounded-xl mb-4 ${isDark ? "bg-red-500/8 border border-red-500/15" : "bg-red-50 border border-red-100"}`}>
              <i className={`ri-error-warning-line text-xs mt-0.5 flex-shrink-0 ${isDark ? "text-red-400" : "text-red-500"}`} />
              <p className={`font-inter text-xs ${isDark ? "text-red-400" : "text-red-600"}`}>{errorMsg}</p>
            </div>
          )}

          {/* Action buttons row */}
          {cancelStep === "idle" && (
            <div className="flex items-center gap-3 flex-wrap">
              <button
                type="button"
                onClick={() => openPortal()}
                disabled={portalLoading}
                className={`flex items-center gap-2 px-4 py-2.5 rounded-xl font-inter text-sm font-medium whitespace-nowrap cursor-pointer transition-all duration-200 disabled:opacity-50 border ${
                  isDark
                    ? "border-white/10 text-white hover:bg-white/6"
                    : "border-black/10 text-[#111] hover:bg-black/4"
                }`}
              >
                {portalLoading
                  ? <i className="ri-loader-4-line animate-spin text-xs" />
                  : <i className="ri-external-link-line text-xs" />}
                {portalLoading ? "Opening…" : "Manage Subscription"}
              </button>
              <button
                type="button"
                onClick={() => { setErrorMsg(null); setCancelStep("confirming"); }}
                className={`font-inter text-xs cursor-pointer transition-colors duration-200 whitespace-nowrap ${
                  isDark ? "text-[#444] hover:text-red-400" : "text-[#bbb] hover:text-red-500"
                }`}
              >
                Cancel subscription
              </button>
            </div>
          )}

          {/* Loading while redirecting to portal cancel flow */}
          {cancelStep === "loading" && (
            <div className="flex items-center gap-2">
              <i className={`ri-loader-4-line animate-spin text-sm ${isDark ? "text-[#555]" : "text-[#bbb]"}`} />
              <p className={`font-inter text-xs ${isDark ? "text-[#555]" : "text-[#999]"}`}>
                Opening cancellation portal…
              </p>
            </div>
          )}
        </div>
      )}

      {/* ── Lifetime: no subscription to manage ─────────────────────────────── */}
      {isLifetime && (
        <div className={`pt-4 border-t flex items-center gap-2.5 ${isDark ? "border-white/5" : "border-black/5"}`}>
          <i className={`ri-infinity-line text-sm ${isDark ? "text-amber-500/50" : "text-amber-500/60"}`} />
          <p className={`font-inter text-xs ${isDark ? "text-[#555]" : "text-[#999]"}`}>
            Lifetime access — no recurring charges or subscriptions to manage.
          </p>
        </div>
      )}

      {/* Upgrade CTA — free users */}
      {!isPro && (
        <div className={`mt-2 flex items-center justify-between gap-4 p-4 rounded-xl ${isDark ? "bg-amber-500/6 border border-amber-500/15" : "bg-amber-50 border border-amber-100"}`}>
          <div>
            <p className={`font-syne font-semibold text-sm mb-0.5 ${isDark ? "text-amber-300" : "text-amber-700"}`}>Upgrade to Pro</p>
            <p className={`font-inter text-xs ${isDark ? "text-amber-400/70" : "text-amber-600/80"}`}>Unlock full tracking, analytics, and all features at launch.</p>
          </div>
          <a
            href="/product#pricing-plans"
            className={`flex-shrink-0 flex items-center gap-1.5 px-4 py-2 rounded-lg font-inter text-xs font-medium whitespace-nowrap cursor-pointer transition-all duration-200 ${
              isDark ? "bg-amber-500 text-black hover:bg-amber-400" : "bg-amber-500 text-white hover:bg-amber-600"
            }`}
          >
            <i className="ri-vip-crown-line text-[11px]" />
            View Plans
          </a>
        </div>
      )}
    </SettingsCard>
  );
}

// ─── Account Section ───────────────────────────────────────────────────────────
function AccountSection() {
  const { isDark } = useTheme();
  const { profile, user } = useAuth();

  const memberSince = profile?.created_at
    ? new Date(profile.created_at).toLocaleDateString("en-US", { month: "long", year: "numeric" })
    : "\u2014";

  const rows = [
    { label: "Account ID", value: user?.id?.slice(0, 16).toUpperCase() ?? "\u2014", mono: true },
    { label: "Member Since", value: memberSince, mono: false },
  ];

  return (
    <SettingsCard title="Account" icon="ri-account-circle-line">
      <div className="space-y-0">
        {rows.map((row, i) => (
          <div
            key={row.label}
            className={`flex items-center justify-between py-3.5 ${
              i < rows.length - 1 ? `border-b ${isDark ? "border-white/5" : "border-black/5"}` : ""
            }`}
          >
            <span className={`font-inter text-sm ${isDark ? "text-[#555]" : "text-[#999]"}`}>{row.label}</span>
            <span className={`font-inter text-sm ${row.mono ? "font-mono text-xs tracking-wider" : "font-medium"} ${isDark ? "text-[#888]" : "text-[#555]"}`}>{row.value}</span>
          </div>
        ))}
      </div>
    </SettingsCard>
  );
}
